import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';
import ImageDropZone from './ImageDropZone';

const MAX_CHARS = 255;
const MAX_VERSO_BLOCKS = 3;

interface VersoBlock {
  id: string;
  content: string;
}

interface CharCounterProps {
  count: number;
  max: number;
}

function CharCounter({ count, max }: CharCounterProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
      <Chip
        label={`${count} / ${max}`}
        size="small"
        color={count >= max * 0.9 ? 'warning' : 'default'}
        sx={{ fontSize: '0.68rem', height: 18 }}
      />
      {count >= max && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <WarningAmberIcon sx={{ fontSize: '0.8rem', color: 'warning.main' }} />
          <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.7rem' }}>Limite atteinte</Typography>
        </Box>
      )}
    </Box>
  );
}

interface TikeoCreationSectionProps {
  initialPayload?: Record<string, unknown> | null;
  onPayloadChange?: (payload: Record<string, unknown>) => void;
}

function TikeoCreationSection({ initialPayload, onPayloadChange }: TikeoCreationSectionProps) {
  const { sidebarColor } = useLayoutPreferences();

  const [title, setTitle] = useState(() => (initialPayload?.title as string) ?? '');
  const [message, setMessage] = useState(() => (initialPayload?.message as string) ?? '');
  const [rectoPreview, setRectoPreview] = useState<string | undefined>();
  const [versoBlocks, setVersoBlocks] = useState<VersoBlock[]>(
    () => (initialPayload?.versoBlocks as VersoBlock[] | undefined) ?? []
  );

  const onPayloadChangeRef = useRef(onPayloadChange);
  onPayloadChangeRef.current = onPayloadChange;

  useEffect(() => {
    // rectoPreview is only a local blob: URL for the dropped file (nothing uploaded anywhere in
    // this POC yet), so it isn't meaningful to persist — title/message/versoBlocks are.
    onPayloadChangeRef.current?.({ type: 'wallet', title, message, versoBlocks });
  }, [title, message, versoBlocks]);

  const handleRectoFile = (file: File) => {
    const url = URL.createObjectURL(file);
    setRectoPreview(url);
  };

  const addVersoBlock = () => {
    if (versoBlocks.length >= MAX_VERSO_BLOCKS) return;
    setVersoBlocks(prev => [...prev, { id: `block-${Date.now()}`, content: '' }]);
  };

  const removeVersoBlock = (id: string) => {
    setVersoBlocks(prev => prev.filter(b => b.id !== id));
  };

  const updateVersoBlock = (id: string, content: string) => {
    setVersoBlocks(prev => prev.map(b => b.id === id ? { ...b, content } : b));
  };

  const cardSx = {
    border: '1px solid #e9ecef',
    borderRadius: 2,
    p: 2.5,
    mb: 2.5,
    backgroundColor: '#fff',
  };

  const sectionTitleSx = {
    fontWeight: 700,
    color: sidebarColor,
    letterSpacing: 0.3,
    mb: 0.25,
    fontSize: '0.88rem',
    textTransform: 'uppercase' as const,
  };

  const subtitleSx = {
    fontSize: '0.78rem',
    color: '#6c757d',
    mb: 2,
    display: 'block' as const,
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={cardSx}>
        <Typography variant="subtitle2" sx={sectionTitleSx}>
          Notification
        </Typography>
        <Typography variant="caption" sx={subtitleSx}>
          Paramétrez le message qui sera envoyé par notification push.
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5, fontSize: '0.82rem' }}>
              Titre de la notification{' '}
              <Typography component="span" variant="caption" color="text.secondary">
                ({MAX_CHARS} caractères maximum) :
              </Typography>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: '0.75rem', fontStyle: 'italic' }}>
              Le titre apparaît au verso de la carte Android.
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={title}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setTitle(e.target.value.slice(0, MAX_CHARS))}
              placeholder="Titre de la notification..."
              inputProps={{ maxLength: MAX_CHARS }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
            <CharCounter count={title.length} max={MAX_CHARS} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5, fontSize: '0.82rem' }}>
              Message de la notification{' '}
              <Typography component="span" variant="caption" color="text.secondary">
                ({MAX_CHARS} caractères maximum) :
              </Typography>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: '0.75rem', fontStyle: 'italic' }}>
              Le message apparaît dans la notification du téléphone et dans le premier bloc au verso de la carte.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              size="small"
              value={message}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setMessage(e.target.value.slice(0, MAX_CHARS))}
              placeholder="Corps du message..."
              inputProps={{ maxLength: MAX_CHARS }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
            <CharCounter count={message.length} max={MAX_CHARS} />

            <Alert
              icon={<WarningAmberIcon fontSize="small" />}
              severity="warning"
              sx={{ mt: 1.5, borderRadius: 1.5, fontSize: '0.75rem', py: 0.5 }}
            >
              Ne pas intégrer de texte en HTML, ni lien, ni saut de ligne
            </Alert>
          </Grid>
        </Grid>
      </Box>

      <Box sx={cardSx}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.25 }}>
          <Typography variant="subtitle2" sx={sectionTitleSx}>
            Mettre à jour la carte
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            (facultatif)
          </Typography>
        </Box>
        <Typography variant="caption" sx={subtitleSx}>
          Lors de l'envoi de la notification, le recto et le verso de la carte peuvent être modifiés le temps de la notification.
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5, color: sidebarColor, fontSize: '0.85rem' }}>
              Modifier le recto de la carte :
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontSize: '0.75rem' }}>
              Si vous ajoutez un visuel, celui-ci remplacera le visuel initial de la carte le temps de la notification.
            </Typography>
            <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.75, fontSize: '0.78rem' }}>
              Image mise en avant :
            </Typography>
            <ImageDropZone
              label="Recto"
              onFile={handleRectoFile}
              preview={rectoPreview}
              sidebarColor={sidebarColor}
              hint="Taille de l'image : 750px × 246px · Formats acceptés : .jpeg ou .png · max 10Mo"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 1 }}>
            <Divider orientation="vertical" sx={{ minHeight: 100, mx: 'auto' }} />
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" fontWeight={700} sx={{ color: sidebarColor, fontSize: '0.85rem' }}>
                Modifier le verso de la carte :
              </Typography>
              <Tooltip title={versoBlocks.length >= MAX_VERSO_BLOCKS ? `Maximum ${MAX_VERSO_BLOCKS} blocs` : 'Ajouter un bloc'}>
                <span>
                  <Button
                    size="small"
                    startIcon={<AddIcon fontSize="small" />}
                    disabled={versoBlocks.length >= MAX_VERSO_BLOCKS}
                    onClick={addVersoBlock}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.78rem',
                      color: sidebarColor,
                      borderColor: sidebarColor,
                      border: '1px solid',
                      borderRadius: 1.5,
                      px: 1.25,
                      py: 0.4,
                      '&:hover': { backgroundColor: `${sidebarColor}10` },
                      '&.Mui-disabled': { opacity: 0.4 },
                    }}
                  >
                    Ajouter un bloc
                  </Button>
                </span>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontSize: '0.75rem' }}>
              Si vous ajoutez des blocs (3 blocs maximum), ceux-ci viendront s'ajouter aux blocs initiaux de la carte.
            </Typography>

            {versoBlocks.length === 0 && (
              <Box
                sx={{
                  border: '1px dashed #dee2e6',
                  borderRadius: 1.5,
                  p: 2,
                  textAlign: 'center',
                  backgroundColor: '#fafafa',
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                  Aucun bloc ajouté. Cliquez sur « Ajouter un bloc » pour commencer.
                </Typography>
              </Box>
            )}

            {versoBlocks.map((block, idx) => (
              <Box
                key={block.id}
                sx={{
                  border: '1px solid #e9ecef',
                  borderRadius: 1.5,
                  p: 1.5,
                  mb: 1,
                  backgroundColor: '#fafafa',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: '0.72rem', letterSpacing: 0.3 }}>
                    BLOC {idx + 1}
                  </Typography>
                  <IconButton size="small" onClick={() => removeVersoBlock(block.id)} sx={{ color: '#dc3545', p: 0.25 }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  placeholder="Contenu du bloc..."
                  value={block.content}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => updateVersoBlock(block.id, e.target.value.slice(0, MAX_CHARS))}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' } }}
                />
                <CharCounter count={block.content.length} max={MAX_CHARS} />
              </Box>
            ))}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default TikeoCreationSection;
